import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Checkbox } from '../../components/ui/checkbox';
import { Trophy, Timer, Euro, Calendar } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api, { apiError, fmtResponse } from '../../lib/api';
import StatsDashboard from './StatsDashboard';

const PERIODS = [
  { v: 'today', l: "Aujourd'hui" },
  { v: '7d', l: '7 jours' },
  { v: '14d', l: '14 jours' },
  { v: '30d', l: '30 jours' },
  { v: 'all', l: 'Tout' },
];

// Liste des fans à qui CE chatteur a envoyé un lien de paiement, + payé / pas payé.
function ChatterLinks({ chatterId, period }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!chatterId) return;
    setRows(null);
    api.get('/reports/chatter-links', { params: { chatter_id: chatterId, period: period || '7d' } })
      .then(({ data }) => setRows(data || [])).catch(() => setRows([]));
  }, [chatterId, period]);
  const fmt = (iso) => { try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
  const paidCount = (rows || []).filter(r => r.paid).length;
  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-semibold text-white">Liens de paiement envoyés</div>
        {rows && rows.length > 0 && <div className="text-[11px] text-slate-400">{paidCount}/{rows.length} ont payé</div>}
      </div>
      <div className="text-[11px] text-slate-500 mb-3">Les fans à qui ce chatteur a envoyé un lien — payé ou pas (sur la période choisie).</div>
      {rows === null ? <div className="text-xs text-slate-500">Chargement…</div>
        : rows.length === 0 ? <div className="text-xs text-slate-500">Aucun lien de paiement envoyé sur cette période.</div>
          : (
            <div className="divide-y divide-white/5 max-h-[320px] overflow-y-auto scrollbar-thin">
              {rows.map((r) => (
                <div key={r.conversation_id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="text-white truncate">{r.fan}{r.telegram ? <span className="text-slate-500 text-xs"> · @{String(r.telegram).replace(/^@/, '')}</span> : null}</div>
                    <div className="text-[11px] text-slate-500">lien envoyé · {fmt(r.link_sent_at)}</div>
                  </div>
                  {r.paid
                    ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 shrink-0">Payé · {(r.amount || 0).toLocaleString('fr-FR')} €</span>
                    : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 shrink-0">Pas payé</span>}
                </div>
              ))}
            </div>
          )}
    </div>
  );
}

export default function EmployeeReport() {
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [includeOwners, setIncludeOwners] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [fromDate, setFromDate] = useState(''); // plage perso (jours locaux) — prioritaire sur `period`
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const customRange = !!(fromDate || toDate);
  // Click-through: an employee's full visual dashboard in a modal.
  const [detailId, setDetailId] = useState(null);
  const [detailName, setDetailName] = useState('');
  const [detailData, setDetailData] = useState({});
  const [detailPeriod, setDetailPeriod] = useState('7d');
  const openDetail = useCallback(async (cid, name, p) => {
    setDetailId(cid); setDetailName(name); setDetailPeriod(p);
    try {
      const { data } = await api.get(`/reports/employee-stats/${cid}`, { params: { tz_offset: new Date().getTimezoneOffset(), ...(p === 'all' ? {} : { period: p }) } });
      setDetailData(data || {});
    } catch (e) { toast.error(apiError(e, 'Échec du chargement')); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    // Plage perso prioritaire ; sinon période prédéfinie. tz_offset → fenêtre dans l'heure locale.
    const params = (fromDate || toDate)
      ? { tz_offset: new Date().getTimezoneOffset(), ...(fromDate ? { from_date: fromDate } : {}), ...(toDate ? { to_date: toDate } : {}) }
      : (period === 'all' ? { tz_offset: new Date().getTimezoneOffset() } : { tz_offset: new Date().getTimezoneOffset(), period });
    try {
      const [r, a] = await Promise.all([
        api.get('/reports/employee', { params }),
        api.get('/accounts'),
      ]);
      setRows(r.data);
      setAccounts(a.data);
    } catch (e) { toast.error(apiError(e, 'Échec du chargement du rapport')); }
    finally { setLoading(false); }
  }, [period, fromDate, toDate]);
  useEffect(() => { load(); }, [load]);

  // Ouverture auto du détail d'un chatteur quand on arrive depuis le tableau de bord (?chatter=ID).
  const [searchParams] = useSearchParams();
  const autoOpened = useRef(false);
  useEffect(() => {
    const cid = searchParams.get('chatter');
    if (!cid || autoOpened.current || !rows.length) return;
    const row = rows.find((r) => r.chatter_id === cid && !r.bot && !r.unattributed);
    if (row) { autoOpened.current = true; openDetail(cid, row.name, '7d'); }
  }, [searchParams, rows, openDetail]);

  const filtered = useMemo(() => includeOwners ? rows : rows.filter(r => !['Owner', 'Co-owner'].includes(r.role)), [rows, includeOwners]);
  // Podium calculé depuis les MÊMES données que le tableau (même période, mêmes chiffres) —
  // l'ancien endpoint dédié tronquait les ventes et ignorait la période (podium faux).
  const top = useMemo(() => rows
    .filter(r => r.role === 'Chatter' && !r.unattributed && !r.bot && (r.total_sales || 0) > 0)
    .sort((a, b) => (b.total_sales || 0) - (a.total_sales || 0))
    .slice(0, 3), [rows]);
  const totals = useMemo(() => ({
    messages: filtered.reduce((s, r) => s + (r.messages || 0), 0),
    sales_count: filtered.reduce((s, r) => s + r.sales_count, 0),
    total_sales: filtered.reduce((s, r) => s + r.total_sales, 0),
  }), [filtered]);

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="employee-report-page">
      <PageHeader title="Rapport employés" subtitle="Performance par employé — messages, ventes et chiffre d'affaires" onRefresh={load} />

      {/* Period filter */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="period-bar">
        <Calendar size={14} className="text-slate-500 ml-1" />
        {PERIODS.map(p => (
          <button
            key={p.v}
            data-testid={`period-${p.v}`}
            onClick={() => { setFromDate(''); setToDate(''); setPeriod(p.v); }}
            className={`px-3 h-8 rounded-lg text-xs font-medium transition-colors ${!customRange && period === p.v ? 'bg-indigo-500 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10 border border-white/10'}`}
          >
            {p.l}
          </button>
        ))}
        <span className="text-slate-600 mx-1 text-xs">|</span>
        <input type="date" data-testid="report-from-date" value={fromDate} onChange={e => setFromDate(e.target.value)} title="Du" className={`bg-white/5 border rounded-lg px-2 h-8 text-xs text-white outline-none ${customRange ? 'border-indigo-400/40' : 'border-white/10'}`} />
        <span className="text-xs text-slate-500">→</span>
        <input type="date" data-testid="report-to-date" value={toDate} onChange={e => setToDate(e.target.value)} title="Au" className={`bg-white/5 border rounded-lg px-2 h-8 text-xs text-white outline-none ${customRange ? 'border-indigo-400/40' : 'border-white/10'}`} />
        {customRange && (
          <button type="button" onClick={() => { setFromDate(''); setToDate(''); }} className="px-2.5 h-8 rounded-lg text-xs text-indigo-300 bg-indigo-500/10 border border-indigo-400/30 hover:bg-indigo-500/20">Effacer ✕</button>
        )}
        {loading && <span className="text-xs text-slate-400 flex items-center gap-1.5 ml-1"><span className="w-3 h-3 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" /> chargement…</span>}
      </div>

      {/* Top performers leaderboard */}
      {top.length > 0 && (
        <div className="rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/[0.04] to-transparent p-4 mb-4" data-testid="top-performers">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={15} className="text-amber-400" />
            <span className="text-sm font-semibold text-white">Meilleurs chatteurs</span>
            <span className="text-[10px] text-slate-500">sur la période choisie — mêmes chiffres que le tableau</span>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {top.slice(0, 3).map((t, i) => (
              <div key={t.chatter_id} className={`rounded-xl border p-3.5 flex items-center gap-3 ${i === 0 ? 'border-amber-400/40 bg-amber-500/[0.06]' : i === 1 ? 'border-slate-400/30 bg-white/[0.03]' : 'border-orange-700/40 bg-white/[0.02]'}`} data-testid={`top-performer-${i + 1}`}>
                <div className={`text-lg font-bold w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${i === 0 ? 'bg-amber-400/20 text-amber-300' : i === 1 ? 'bg-slate-400/20 text-slate-300' : 'bg-orange-700/20 text-orange-400'}`}>{i + 1}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-white truncate">{t.name}</div>
                  <div className="flex items-center gap-3 text-[11px] mt-0.5">
                    <span className="text-emerald-400 font-bold flex items-center gap-1"><Euro size={10} /> {(t.total_sales || 0).toLocaleString('fr-FR')} €</span>
                    <span className="text-slate-400">{t.sales_count} ventes</span>
                    <span className="text-sky-300">panier {t.avg_basket ?? 0} €</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 mb-4 flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
          <Checkbox data-testid="include-owners-checkbox" checked={includeOwners} onCheckedChange={setIncludeOwners} />
          Inclure les owners & co-owners
        </label>
        <div className="ml-auto flex items-center gap-4 text-xs text-slate-400">
          <span>Messages : <span className="text-white font-semibold">{totals.messages}</span></span>
          <span>Ventes : <span className="text-white font-semibold">{totals.sales_count}</span></span>
          <span>CA : <span className="text-emerald-400 font-bold" data-testid="report-total-revenue">{totals.total_sales.toLocaleString()} €</span></span>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <th className="px-5 py-3 font-semibold">Employé</th>
              <th className="px-5 py-3 font-semibold">Rôle</th>
              <th className="px-5 py-3 font-semibold">Comptes</th>
              <th className="px-5 py-3 font-semibold text-right">Messages</th>
              <th className="px-5 py-3 font-semibold text-right" title="Liens de paiement envoyés">Liens</th>
              <th className="px-5 py-3 font-semibold text-right">Nb ventes</th>
              <th className="px-5 py-3 font-semibold text-right">Total ventes</th>
              <th className="px-5 py-3 font-semibold text-right" title="Ventes approuvées ÷ fans contactés">Conv.</th>
              <th className="px-5 py-3 font-semibold text-right" title="Montant moyen € par vente">Panier moy.</th>
              <th className="px-5 py-3 font-semibold text-right" title="CA vs période précédente">Tendance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.map(r => { const isUn = r.unattributed; const isBot = r.bot; const fixe = isUn || isBot; return (
              <tr key={r.chatter_id} onClick={fixe ? undefined : () => openDetail(r.chatter_id, r.name, '7d')} className={isBot ? 'bg-violet-500/[0.04]' : isUn ? 'bg-amber-500/[0.04]' : 'hover:bg-white/[0.04] cursor-pointer'} data-testid={`report-row-${r.chatter_id}`} title={isBot ? "Ventes faites par le bot d'accueil" : isUn ? 'Ventes réelles non rattachées à un chatteur — à attribuer à la main' : 'Voir le tableau de bord'}>
                <td className="px-5 py-3">
                  {isBot ? (
                    // Bot d'accueil : ses ventes à part, jamais « à rattacher » ni dans le classement des chatteurs.
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-violet-500/15 flex items-center justify-center text-sm">🤖</div>
                      <span className="text-sm font-medium text-violet-200">Bot</span>
                    </div>
                  ) : isUn ? (
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-amber-500/15 flex items-center justify-center text-amber-300 text-xs font-bold">?</div>
                      <span className="text-sm font-medium text-amber-200">Non attribué <span className="text-[10px] text-slate-500">(à rattacher)</span></span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2.5">
                      <Avatar className="w-8 h-8"><AvatarFallback className="bg-gradient-to-br from-indigo-500 to-violet-500 text-white text-xs">{r.name.split(' ').map(x => x[0]).join('')}</AvatarFallback></Avatar>
                      <span className="text-sm font-medium text-white">{r.name}</span>
                    </div>
                  )}
                </td>
                <td className="px-5 py-3"><Badge variant="outline" className="border-white/10 bg-white/5 text-slate-300 text-[10px]">{r.role}</Badge></td>
                <td className="px-5 py-3">
                  <div className="flex -space-x-1.5">
                    {(r.accounts || []).map(aid => {
                      const a = accounts.find(x => x.id === aid);
                      return a ? <div key={aid} title={a.name} className={`w-6 h-6 rounded-md bg-gradient-to-br ${a.color} flex items-center justify-center text-[9px] text-white font-bold border border-[#08080A]`}>{a.initial}</div> : null;
                    })}
                  </div>
                </td>
                <td className="px-5 py-3 text-right text-sm text-slate-300">{r.messages ?? 0}</td>
                <td className="px-5 py-3 text-right text-sm text-slate-400">{r.payment_links ?? 0}</td>
                <td className="px-5 py-3 text-right text-sm text-slate-300">{r.sales_count}</td>
                <td className="px-5 py-3 text-right text-sm font-bold text-emerald-400">{r.total_sales.toLocaleString()} €</td>
                <td className="px-5 py-3 text-right text-sm text-sky-300">{r.conversion_rate ?? 0}%</td>
                <td className="px-5 py-3 text-right text-sm text-slate-300">{r.avg_basket ?? 0} €</td>
                <td className={`px-5 py-3 text-right text-sm font-medium ${r.progression == null ? 'text-slate-500' : r.progression >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {r.progression == null ? '—' : `${r.progression > 0 ? '+' : ''}${r.progression}%`}
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detailId} onOpenChange={(o) => { if (!o) setDetailId(null); }}>
        <DialogContent className="bg-[#0C0C0F] border-white/10 text-slate-200 max-w-4xl max-h-[88vh] overflow-y-auto scrollbar-thin">
          <DialogHeader><DialogTitle className="text-white">Tableau de bord — {detailName}</DialogTitle></DialogHeader>
          <StatsDashboard data={detailData} period={detailPeriod} onPeriod={(p) => openDetail(detailId, detailName, p)} editable={false} />
          <ChatterLinks chatterId={detailId} period={detailPeriod} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
